import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

export const CONTACT_ROLES = ["venditore", "acquirente", "proprietario", "inquilino", "costruttore"] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

const Contact = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  roles: z.array(z.string()),
  notes: z.string().nullable(),
  source: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Contact = z.infer<typeof Contact>;
const ContactsResponse = z.object({ items: z.array(Contact), total: z.number() });

export type ContactInput = {
  name: string;
  email?: string | null;
  phone?: string | null;
  roles?: ContactRole[];
  notes?: string | null;
};

export function useContacts(filters: { q?: string; role?: string } = {}) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["contacts", filters],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/contacts", token!, ContactsResponse, {
      query: { q: filters.q || undefined, role: filters.role || undefined, page_size: 200 },
    }),
  });
}

export function useCreateContact() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput) =>
      apiFetch("/api/v1/admin/contacts", session!.access_token, Contact, { method: "POST", body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export function usePatchContact() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<ContactInput> & { id: string }) =>
      apiFetch(`/api/v1/admin/contacts/${id}`, session!.access_token, Contact, { method: "PATCH", body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}
